import { createBrowserRouter, Navigate } from 'react-router-dom';
import WelcomePage from '@/pages/welcome.page';
import SignInPage from '@/pages/sign-in.page';
import Verify2faPage from '@/pages/verify-2fa.page';
import MfaEnrollPage from '@/pages/mfa/enroll.page';
import { ProtectedRoute, PublicRoute } from '@/components/auth/protected-route.component';
import ForgotPasswordPage from '@/pages/forgot-password.page';
import ResetPasswordPage from '@/pages/reset-password.page';
import AcceptInvitePage from '@/pages/accept-invite.page';
import ChangePasswordPage from '@/pages/change-password.page';
import { RootLayout } from '@/components/layout/root-layout.component';
import { RootRedirect } from '@/components/auth/root-redirect.component';

// Lazy load pages to reduce initial bundle size
import { lazy, Suspense } from 'react';

// Suspense fallback component
function PageLoader() {
  return (
    <div className="flex h-screen items-center justify-center">
      <div className="text-center">
        <div className="mb-4 inline-block h-8 w-8 animate-spin rounded-full border-4 border-gray-300 border-t-blue-600" />
        <p className="text-gray-600">Loading page...</p>
      </div>
    </div>
  );
}

const DashboardPage = lazy(() => import('@/pages/dashboard.page'));
const NotFoundPage = lazy(() => import('@/pages/not-found.page'));
const ForbiddenPage = lazy(() => import('@/pages/forbidden.page'));
const ErrorPage = lazy(() => import('@/pages/error.page'));

// Personnel pages
const PersonnelEmployeeListPage = lazy(() => import('@/pages/personnel/employee-list.page'));
const PersonnelEmployeeDetailPage = lazy(() => import('@/pages/personnel/employee-detail.page'));
const PersonnelEmploymentListPage = lazy(() => import('@/pages/personnel/employment-list.page'));
const PersonnelEmploymentDetailPage = lazy(() => import('@/pages/personnel/employment-detail.page'));
const PersonnelDeductionListPage = lazy(() => import('@/pages/personnel/deduction-list.page'));
const PersonnelDeductionsOverviewPage = lazy(() => import('@/pages/personnel/deductions-overview.page'));
const PersonnelLeaveManagementPage = lazy(() => import('@/pages/personnel/leave-management.page'));
const PersonnelDeductionTypesPage = lazy(() => import('@/pages/personnel/deduction-types.page'));

// Geography pages
const GeographyCountriesPage = lazy(() => import('@/pages/geography/countries.page'));
const GeographyRegionsPage = lazy(() => import('@/pages/geography/regions.page'));
const GeographyDistrictsPage = lazy(() => import('@/pages/geography/districts.page'));

// Estates pages
const EstatesListPage = lazy(() => import('@/pages/estates/list.page'));
const EstatesDetailPage = lazy(() => import('@/pages/estates/detail.page'));
const EstateCreatePage = lazy(() => import('@/pages/estates/estate-create.page'));
const EstateEditPage = lazy(() => import('@/pages/estates/estate-edit.page'));
const EstatesBlocksPage = lazy(() => import('@/pages/estates/blocks.page'));
const BlockCreatePage = lazy(() => import('@/pages/estates/block-create.page'));
const BlockDetailPage = lazy(() => import('@/pages/estates/block-detail.page'));
const BlockEditPage = lazy(() => import('@/pages/estates/block-edit.page'));
const EstatesOperationsPage = lazy(() => import('@/pages/estates/operations.page'));
const EstatesOperationRatesPage = lazy(() => import('@/pages/estates/operation-rates.page'));
const EstatesAverageWeightsPage = lazy(() => import('@/pages/estates/average-weights.page'));

// Payroll pages
const PayrollCyclesPage = lazy(() => import('@/pages/payroll/cycles.page'));
const PayrollCycleDetailPage = lazy(() => import('@/pages/payroll/cycle-detail.page'));
const PayrollCycleEstateDetailPage = lazy(() => import('@/pages/payroll/cycle-estate-detail.page'));
const PayrollCycleWorkerDetailPage = lazy(() => import('@/pages/payroll/cycle-worker-detail.page'));
const PayrollCycleWorkerEstatesPage = lazy(() => import('@/pages/payroll/cycle-worker-estates.page'));
const PayrollCycleWorkerEstateTasksPage = lazy(() => import('@/pages/payroll/cycle-worker-estate-tasks.page'));
const PayrollMillingBatchesPage = lazy(() => import('@/pages/payroll/milling-batches.page'));
const PayrollMillingBatchDetailPage = lazy(() => import('@/pages/payroll/milling-batch-detail.page'));
const PayrollCpoInventoryPage  = lazy(() => import('@/pages/payroll/cpo-inventory.page'));
const PayrollCpkoInventoryPage = lazy(() => import('@/pages/payroll/cpko-inventory.page'));
const PayrollPkcInventoryPage  = lazy(() => import('@/pages/payroll/pkc-inventory.page'));
const PayrollOperationalExpensesPage = lazy(() => import('@/pages/payroll/operational-expenses.page'));
const PayrollOperationalExpenseDetailPage = lazy(() => import('@/pages/payroll/operational-expense-detail.page'));
const PayrollOperationalExpenseReportsPage = lazy(() => import('@/pages/payroll/operational-expense-reports.page'));
const PayrollLoanDeductionsPage = lazy(() => import('@/pages/payroll/loan-deductions.page'));
const PayrollFarmSalesPage            = lazy(() => import('@/pages/payroll/farm-sales.page'));
const PayrollFarmSalesOverviewPage    = lazy(() => import('@/pages/payroll/farm-sales-overview.page'));
const PayrollFarmSaleConfigPage       = lazy(() => import('@/pages/payroll/farm-sale-config.page'));
const PayrollPrepaymentFundsPage      = lazy(() => import('@/pages/payroll/prepayment-funds.page'));

// Expenses pages
const ExpensesListPage = lazy(() => import('@/pages/expenses/list.page'));

// Inventory pages
const InventoryItemsPage = lazy(() => import('@/pages/inventory/items.page'));
const InventoryItemDetailPage = lazy(() => import('@/pages/inventory/item-detail.page'));
const InventoryVendorsPage = lazy(() => import('@/pages/inventory/vendors.page'));
const InventoryVendorDetailPage = lazy(() => import('@/pages/inventory/vendor-detail.page'));
const InventoryStockPage = lazy(() => import('@/pages/inventory/stock.page'));
const InventoryProcurementPage = lazy(() => import('@/pages/inventory/procurement.page'));
const InventoryProcurementDetailPage = lazy(() => import('@/pages/inventory/procurement-detail.page'));
const InventoryRequisitionsPage = lazy(() => import('@/pages/inventory/requisitions.page'));
const InventoryRequisitionDetailPage = lazy(() => import('@/pages/inventory/requisition-detail.page'));
const InventoryItemCategoriesPage = lazy(() => import('@/pages/inventory/item-categories.page'));
const InventoryStoresPage = lazy(() => import('@/pages/inventory/stores.page'));

// Administration pages
const AdministrationUsersPage = lazy(() => import('@/pages/administration/users.page'));
const AdministrationRolesPage = lazy(() => import('@/pages/administration/roles.page'));
const AdministrationWorkingYearsPage = lazy(() => import('@/pages/administration/working-years.page'));

// Reporting pages
const ReportingPage = lazy(() => import('@/pages/reporting/index.page'));
const ReportingExpensesPage = lazy(() => import('@/pages/reporting/expenses.page'));
const ReportingPayrollPage = lazy(() => import('@/pages/reporting/payroll.page'));
// Payroll reports
const ReportPayrollSummaryPage = lazy(() => import('@/pages/reporting/payroll/payroll-summary.page'));
const ReportPayslipPage = lazy(() => import('@/pages/reporting/payroll/payslip.page'));
const ReportTaskDetailPage = lazy(() => import('@/pages/reporting/payroll/task-detail.page'));
const ReportMillingReportPage = lazy(() => import('@/pages/reporting/payroll/milling-report.page'));
const ReportOperationRatesCardPage = lazy(() => import('@/pages/reporting/payroll/operation-rates-card.page'));
const ReportProductionReportPage = lazy(() => import('@/pages/reporting/payroll/production-report.page'));
const ReportCostOfProductionPage = lazy(() => import('@/pages/reporting/payroll/cost-of-production-report.page'));
const ReportLabourProductivityPage = lazy(() => import('@/pages/reporting/payroll/labour-productivity-report.page'));
const ReportBlockPerformancePage = lazy(() => import('@/pages/reporting/payroll/block-performance-report.page'));
const ReportAnnualSummaryPage = lazy(() => import('@/pages/reporting/payroll/annual-summary-report.page'));
// Personnel reports
const ReportEmployeeRegisterPage = lazy(() => import('@/pages/reporting/personnel/employee-register.page'));
const ReportHeadcountPage = lazy(() => import('@/pages/reporting/personnel/headcount.page'));
const ReportAdvancesLoansPage = lazy(() => import('@/pages/reporting/personnel/advances-loans.page'));
const ReportDeductionAgingPage = lazy(() => import('@/pages/reporting/personnel/deduction-aging.page'));
const ReportEmploymentHistoryPage = lazy(() => import('@/pages/reporting/personnel/employment-history.page'));
// Estates reports
const ReportEstateProfilePage = lazy(() => import('@/pages/reporting/estates/estate-profile.page'));
const ReportBlockProductivityPage = lazy(() => import('@/pages/reporting/estates/block-productivity.page'));
const ReportOperationPerformancePage = lazy(() => import('@/pages/reporting/estates/operation-performance.page'));
// Inventory reports
const ReportStockLevelsPage = lazy(() => import('@/pages/reporting/inventory/stock-levels.page'));
const ReportStockMovementPage = lazy(() => import('@/pages/reporting/inventory/stock-movement.page'));
const ReportProcurementPage = lazy(() => import('@/pages/reporting/inventory/procurement.page'));
const ReportRequisitionsPage = lazy(() => import('@/pages/reporting/inventory/requisitions.page'));
const ReportVendorSummaryPage = lazy(() => import('@/pages/reporting/inventory/vendor-summary.page'));
// Expenses reports
const ReportExpenseSummaryPage = lazy(() => import('@/pages/reporting/expenses/expense-summary.page'));
const ReportExpenseDetailPage = lazy(() => import('@/pages/reporting/expenses/expense-detail.page'));
const ReportExpenseByEstatePage = lazy(() => import('@/pages/reporting/expenses/expense-by-estate.page'));
// Validated-cycle history reports
const ReportFarmSalesHistoryPage    = lazy(() => import('@/pages/reporting/payroll/farm-sales-history.page'));
const ReportCycleDeductionsPage     = lazy(() => import('@/pages/reporting/payroll/cycle-deductions.page'));
const ReportPrepaymentsHistoryPage  = lazy(() => import('@/pages/reporting/payroll/prepayments-history.page'));
const ReportPrepaymentAuditPage     = lazy(() => import('@/pages/reporting/payroll/prepayment-audit-report.page'));

// Settings pages
const SettingsSalaryItemsPage = lazy(() => import('@/pages/settings/salary-items.page'));
const SettingsPayrollSettingsPage = lazy(() => import('@/pages/settings/payroll-settings.page'));
const SettingsPreferencesPage = lazy(() => import('@/pages/settings/preferences.page'));
const SettingsMoMoOperatorsPage = lazy(() => import('@/pages/settings/momo-operators.page'));
const SettingsMobileProvisionPage = lazy(() => import('@/pages/settings/mobile-provision.page'));
const SettingsStatutoryRatesPage = lazy(() => import('@/pages/settings/statutory-rates.page'));
const SettingsSecurityPage = lazy(() => import('@/pages/settings/security.page'));
const SettingsSystemSettingsPage = lazy(() => import('@/pages/settings/system-settings.page'));

// Setup pages (new)
const SetupAllowanceTypesPage = lazy(() => import('@/pages/setup/allowance-types.page'));
const SetupSalaryGridPage = lazy(() => import('@/pages/setup/salary-grid.page'));

// Sales pages
const SalesOilPurchasesPage = lazy(() => import('@/pages/sales/oil-purchases.page'));
const SalesOilPurchaseDetailPage = lazy(() => import('@/pages/sales/oil-purchase-detail.page'));
const SalesSaleOrdersPage = lazy(() => import('@/pages/sales/sale-orders.page'));
const SalesSaleOrderDetailPage = lazy(() => import('@/pages/sales/sale-order-detail.page'));
const SalesSaleTypesPage = lazy(() => import('@/pages/sales/sale-types.page'));
const SalesPricingTiersPage = lazy(() => import('@/pages/sales/pricing-tiers.page'));
const SalesSalePricesPage = lazy(() => import('@/pages/sales/sale-prices.page'));
const SalesMarketPricesPage = lazy(() => import('@/pages/sales/market-prices.page'));
const SalesDashboardPage = lazy(() => import('@/pages/sales/dashboard.page'));
const SalesOilAllocationsPage = lazy(() => import('@/pages/sales/oil-allocations.page'));
const SalesOilAllocationDetailPage = lazy(() => import('@/pages/sales/oil-allocation-detail.page'));
const SalesOilSterilizationsPage = lazy(() => import('@/pages/sales/oil-sterilizations.page'));
const SalesOilSterilizationDetailPage = lazy(() => import('@/pages/sales/oil-sterilization-detail.page'));
const SalesFinishedGoodsInventoryPage = lazy(() => import('@/pages/sales/finished-goods-inventory.page'));
const SalesOilPurchaseReportPage = lazy(() => import('@/pages/sales/oil-purchase-report.page'));
const SalesMarketPriceStrategyReportPage = lazy(() => import('@/pages/sales/market-price-strategy-report.page'));
const SalesCustomersPage = lazy(() => import('@/pages/sales/customers.page'));
const SalesSkusPage = lazy(() => import('@/pages/sales/skus.page'));
const SalesBottlingBatchesPage = lazy(() => import('@/pages/sales/bottling-batches.page'));
const SalesBottlingBatchDetailPage = lazy(() => import('@/pages/sales/bottling-batch-detail.page'));
const SalesCartonningBatchesPage = lazy(() => import('@/pages/sales/cartonning-batches.page'));
const SalesCartonningBatchDetailPage = lazy(() => import('@/pages/sales/cartonning-batch-detail.page'));

// Sales reporting pages
const ReportSalesInventoryBalancesPage = lazy(() => import('@/pages/reporting/sales/sales-inventory-balances.page'));
const ReportProductionVsAllocationPage = lazy(() => import('@/pages/reporting/sales/production-vs-allocation.page'));
const ReportBottlingEfficiencyPage = lazy(() => import('@/pages/reporting/sales/bottling-efficiency.page'));
const ReportRejectionRatePage = lazy(() => import('@/pages/reporting/sales/rejection-rate.page'));
const ReportMarginByChannelPage = lazy(() => import('@/pages/reporting/sales/margin-by-channel.page'));
const ReportFgTurnoverPage = lazy(() => import('@/pages/reporting/sales/fg-turnover.page'));

// Search & Help pages
const SearchPage = lazy(() => import('@/pages/search.page'));
const HelpPage = lazy(() => import('@/pages/help.page'));

export const router = createBrowserRouter([
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <RootLayout />
      </ProtectedRoute>
    ),
    errorElement: <ErrorPage />,
    children: [
      // Root redirect
      {
        index: true,
        element: <RootRedirect />,
      },

      // Dashboard (protected)
      {
        path: 'dashboard',
        element: (
          <Suspense fallback={<PageLoader />}>
            <DashboardPage />
          </Suspense>
        ),
      },

      // Personnel routes
      {
        path: 'personnel',
        children: [
          {
            path: 'employees',
            element: (
              <Suspense fallback={<PageLoader />}>
                <PersonnelEmployeeListPage />
              </Suspense>
            ),
          },
          {
            path: 'employees/:id',
            element: (
              <Suspense fallback={<PageLoader />}>
                <PersonnelEmployeeDetailPage />
              </Suspense>
            ),
          },
          {
            path: 'employees/:id/employment',
            element: (
              <Suspense fallback={<PageLoader />}>
                <PersonnelEmploymentDetailPage />
              </Suspense>
            ),
          },
          {
            path: 'employment-list',
            element: (
              <Suspense fallback={<PageLoader />}>
                <PersonnelEmploymentListPage />
              </Suspense>
            ),
          },
          {
            path: 'deduction-list',
            element: (
              <Suspense fallback={<PageLoader />}>
                <PersonnelDeductionListPage />
              </Suspense>
            ),
          },
          {
            path: 'deductions-overview',
            element: (
              <Suspense fallback={<PageLoader />}>
                <PersonnelDeductionsOverviewPage />
              </Suspense>
            ),
          },
          {
            path: 'leave-management',
            element: (
              <Suspense fallback={<PageLoader />}>
                <PersonnelLeaveManagementPage />
              </Suspense>
            ),
          },
        ],
      },

      // Setup routes
      {
        path: 'setup',
        children: [
          {
            path: 'deduction-types',
            element: (
              <Suspense fallback={<PageLoader />}>
                <PersonnelDeductionTypesPage />
              </Suspense>
            ),
          },
          {
            path: 'allowance-types',
            element: (
              <Suspense fallback={<PageLoader />}>
                <SetupAllowanceTypesPage />
              </Suspense>
            ),
          },
          {
            path: 'salary-grid',
            element: (
              <Suspense fallback={<PageLoader />}>
                <SetupSalaryGridPage />
              </Suspense>
            ),
          },
        ],
      },

      // Geography routes
      {
        path: 'geography',
        children: [
          {
            path: 'countries',
            element: (
              <Suspense fallback={<PageLoader />}>
                <GeographyCountriesPage />
              </Suspense>
            ),
          },
          {
            path: 'regions',
            element: (
              <Suspense fallback={<PageLoader />}>
                <GeographyRegionsPage />
              </Suspense>
            ),
          },
          {
            path: 'districts',
            element: (
              <Suspense fallback={<PageLoader />}>
                <GeographyDistrictsPage />
              </Suspense>
            ),
          },
        ],
      },

      // Estates routes
      {
        path: 'estates',
        children: [
          {
            path: '',
            element: (
              <Suspense fallback={<PageLoader />}>
                <EstatesListPage />
              </Suspense>
            ),
          },
          {
            path: 'new',
            element: (
              <Suspense fallback={<PageLoader />}>
                <EstateCreatePage />
              </Suspense>
            ),
          },
          {
            path: ':id',
            element: (
              <Suspense fallback={<PageLoader />}>
                <EstatesDetailPage />
              </Suspense>
            ),
          },
          {
            path: ':id/edit',
            element: (
              <Suspense fallback={<PageLoader />}>
                <EstateEditPage />
              </Suspense>
            ),
          },
          {
            path: 'blocks',
            element: (
              <Suspense fallback={<PageLoader />}>
                <EstatesBlocksPage />
              </Suspense>
            ),
          },
          {
            path: 'blocks/new',
            element: (
              <Suspense fallback={<PageLoader />}>
                <BlockCreatePage />
              </Suspense>
            ),
          },
          {
            path: 'blocks/:id',
            element: (
              <Suspense fallback={<PageLoader />}>
                <BlockDetailPage />
              </Suspense>
            ),
          },
          {
            path: 'blocks/:id/edit',
            element: (
              <Suspense fallback={<PageLoader />}>
                <BlockEditPage />
              </Suspense>
            ),
          },
          {
            path: 'operation-types',
            element: (
              <Suspense fallback={<PageLoader />}>
                <EstatesOperationsPage />
              </Suspense>
            ),
          },
          {
            path: 'operation-rates',
            element: (
              <Suspense fallback={<PageLoader />}>
                <EstatesOperationRatesPage />
              </Suspense>
            ),
          },
          {
            path: 'average-weights',
            element: (
              <Suspense fallback={<PageLoader />}>
                <EstatesAverageWeightsPage />
              </Suspense>
            ),
          },
        ],
      },

      // Payroll routes
      {
        path: 'payroll',
        children: [
          {
            path: 'cycles',
            element: (
              <Suspense fallback={<PageLoader />}>
                <PayrollCyclesPage />
              </Suspense>
            ),
          },
          {
            path: 'cycles/:id',
            element: (
              <Suspense fallback={<PageLoader />}>
                <PayrollCycleDetailPage />
              </Suspense>
            ),
          },
          {
            path: 'cycles/:id/estates/:estateId',
            element: (
              <Suspense fallback={<PageLoader />}>
                <PayrollCycleEstateDetailPage />
              </Suspense>
            ),
          },
          {
            path: 'cycles/:id/estates/:estateId/workers/:employeeId',
            element: (
              <Suspense fallback={<PageLoader />}>
                <PayrollCycleWorkerDetailPage />
              </Suspense>
            ),
          },
          {
            path: 'cycles/:id/workers/:employeeId',
            element: (
              <Suspense fallback={<PageLoader />}>
                <PayrollCycleWorkerEstatesPage />
              </Suspense>
            ),
          },
          {
            path: 'cycles/:id/workers/:employeeId/estates/:estateId',
            element: (
              <Suspense fallback={<PageLoader />}>
                <PayrollCycleWorkerEstateTasksPage />
              </Suspense>
            ),
          },
          {
            path: 'milling-batches',
            element: (
              <Suspense fallback={<PageLoader />}>
                <PayrollMillingBatchesPage />
              </Suspense>
            ),
          },
          {
            path: 'milling-batches/:cycleId',
            element: (
              <Suspense fallback={<PageLoader />}>
                <PayrollMillingBatchDetailPage />
              </Suspense>
            ),
          },
          {
            path: 'cpo-inventory',
            element: <Navigate to="/sales/inventory" replace />,
          },
          {
            path: 'cpko-inventory',
            element: (
              <Suspense fallback={<PageLoader />}>
                <PayrollCpkoInventoryPage />
              </Suspense>
            ),
          },
          {
            path: 'pkc-inventory',
            element: (
              <Suspense fallback={<PageLoader />}>
                <PayrollPkcInventoryPage />
              </Suspense>
            ),
          },
          {
            path: 'operational-expenses',
            element: (
              <Suspense fallback={<PageLoader />}>
                <PayrollOperationalExpensesPage />
              </Suspense>
            ),
          },
          {
            path: 'operational-expenses/reports',
            element: (
              <Suspense fallback={<PageLoader />}>
                <PayrollOperationalExpenseReportsPage />
              </Suspense>
            ),
          },
          {
            path: 'operational-expenses/:id',
            element: (
              <Suspense fallback={<PageLoader />}>
                <PayrollOperationalExpenseDetailPage />
              </Suspense>
            ),
          },
          {
            path: 'loan-deductions',
            element: (
              <Suspense fallback={<PageLoader />}>
                <PayrollLoanDeductionsPage />
              </Suspense>
            ),
          },
          {
            path: 'farm-sales',
            element: (
              <Suspense fallback={<PageLoader />}>
                <PayrollFarmSalesPage />
              </Suspense>
            ),
          },
          {
            path: 'farm-sales-overview',
            element: (
              <Suspense fallback={<PageLoader />}>
                <PayrollFarmSalesOverviewPage />
              </Suspense>
            ),
          },
          {
            path: 'farm-sale-config',
            element: (
              <Suspense fallback={<PageLoader />}>
                <PayrollFarmSaleConfigPage />
              </Suspense>
            ),
          },
          {
            path: 'prepayment-funds',
            element: (
              <Suspense fallback={<PageLoader />}>
                <PayrollPrepaymentFundsPage />
              </Suspense>
            ),
          },
        ],
      },

      // Expenses routes
      {
        path: 'expenses',
        element: (
          <Suspense fallback={<PageLoader />}>
            <ExpensesListPage />
          </Suspense>
        ),
      },

      // Sales routes
      {
        path: 'sales',
        children: [
          {
            path: 'oil-purchases',
            element: (
              <Suspense fallback={<PageLoader />}>
                <SalesOilPurchasesPage />
              </Suspense>
            ),
          },
          {
            path: 'oil-purchases/:id',
            element: (
              <Suspense fallback={<PageLoader />}>
                <SalesOilPurchaseDetailPage />
              </Suspense>
            ),
          },
          {
            path: 'sale-orders',
            element: (
              <Suspense fallback={<PageLoader />}>
                <SalesSaleOrdersPage />
              </Suspense>
            ),
          },
          {
            path: 'sale-orders/:id',
            element: (
              <Suspense fallback={<PageLoader />}>
                <SalesSaleOrderDetailPage />
              </Suspense>
            ),
          },
          {
            path: 'sale-types',
            element: (
              <Suspense fallback={<PageLoader />}>
                <SalesSaleTypesPage />
              </Suspense>
            ),
          },
          {
            path: 'pricing-tiers',
            element: (
              <Suspense fallback={<PageLoader />}>
                <SalesPricingTiersPage />
              </Suspense>
            ),
          },
          {
            path: 'sale-prices',
            element: (
              <Suspense fallback={<PageLoader />}>
                <SalesSalePricesPage />
              </Suspense>
            ),
          },
          {
            path: 'market-prices',
            element: (
              <Suspense fallback={<PageLoader />}>
                <SalesMarketPricesPage />
              </Suspense>
            ),
          },
          {
            path: 'dashboard',
            element: (
              <Suspense fallback={<PageLoader />}>
                <SalesDashboardPage />
              </Suspense>
            ),
          },
          {
            path: 'oil-allocations',
            element: (
              <Suspense fallback={<PageLoader />}>
                <SalesOilAllocationsPage />
              </Suspense>
            ),
          },
          {
            path: 'oil-allocations/:id',
            element: (
              <Suspense fallback={<PageLoader />}>
                <SalesOilAllocationDetailPage />
              </Suspense>
            ),
          },
          {
            path: 'oil-sterilizations',
            element: (
              <Suspense fallback={<PageLoader />}>
                <SalesOilSterilizationsPage />
              </Suspense>
            ),
          },
          {
            path: 'oil-sterilizations/:id',
            element: (
              <Suspense fallback={<PageLoader />}>
                <SalesOilSterilizationDetailPage />
              </Suspense>
            ),
          },
          {
            path: 'finished-goods',
            element: (
              <Suspense fallback={<PageLoader />}>
                <SalesFinishedGoodsInventoryPage />
              </Suspense>
            ),
          },
          {
            path: 'inventory',
            element: (
              <Suspense fallback={<PageLoader />}>
                <PayrollCpoInventoryPage />
              </Suspense>
            ),
          },
          {
            path: 'reports/oil-purchases',
            element: (
              <Suspense fallback={<PageLoader />}>
                <SalesOilPurchaseReportPage />
              </Suspense>
            ),
          },
          {
            path: 'reports/market-price-strategy',
            element: (
              <Suspense fallback={<PageLoader />}>
                <SalesMarketPriceStrategyReportPage />
              </Suspense>
            ),
          },
          {
            path: 'customers',
            element: (
              <Suspense fallback={<PageLoader />}>
                <SalesCustomersPage />
              </Suspense>
            ),
          },
          {
            path: 'skus',
            element: (
              <Suspense fallback={<PageLoader />}>
                <SalesSkusPage />
              </Suspense>
            ),
          },
          {
            path: 'bottling-batches',
            element: (
              <Suspense fallback={<PageLoader />}>
                <SalesBottlingBatchesPage />
              </Suspense>
            ),
          },
          {
            path: 'bottling-batches/:id',
            element: (
              <Suspense fallback={<PageLoader />}>
                <SalesBottlingBatchDetailPage />
              </Suspense>
            ),
          },
          {
            path: 'cartonning-batches',
            element: (
              <Suspense fallback={<PageLoader />}>
                <SalesCartonningBatchesPage />
              </Suspense>
            ),
          },
          {
            path: 'cartonning-batches/:id',
            element: (
              <Suspense fallback={<PageLoader />}>
                <SalesCartonningBatchDetailPage />
              </Suspense>
            ),
          },
        ],
      },

      // Inventory routes
      {
        path: 'inventory',
        children: [
          {
            path: 'items',
            element: (
              <Suspense fallback={<PageLoader />}>
                <InventoryItemsPage />
              </Suspense>
            ),
          },
          {
            path: 'items/:publicId',
            element: (
              <Suspense fallback={<PageLoader />}>
                <InventoryItemDetailPage />
              </Suspense>
            ),
          },
          {
            path: 'vendors',
            element: (
              <Suspense fallback={<PageLoader />}>
                <InventoryVendorsPage />
              </Suspense>
            ),
          },
          {
            path: 'vendors/:publicId',
            element: (
              <Suspense fallback={<PageLoader />}>
                <InventoryVendorDetailPage />
              </Suspense>
            ),
          },
          {
            path: 'stock',
            element: (
              <Suspense fallback={<PageLoader />}>
                <InventoryStockPage />
              </Suspense>
            ),
          },
          {
            path: 'procurement',
            element: (
              <Suspense fallback={<PageLoader />}>
                <InventoryProcurementPage />
              </Suspense>
            ),
          },
          {
            path: 'procurement/:id',
            element: (
              <Suspense fallback={<PageLoader />}>
                <InventoryProcurementDetailPage />
              </Suspense>
            ),
          },
          {
            path: 'requisitions',
            element: (
              <Suspense fallback={<PageLoader />}>
                <InventoryRequisitionsPage />
              </Suspense>
            ),
          },
          {
            path: 'requisitions/:id',
            element: (
              <Suspense fallback={<PageLoader />}>
                <InventoryRequisitionDetailPage />
              </Suspense>
            ),
          },
          {
            path: 'item-categories',
            element: (
              <Suspense fallback={<PageLoader />}>
                <InventoryItemCategoriesPage />
              </Suspense>
            ),
          },
          {
            path: 'stores',
            element: (
              <Suspense fallback={<PageLoader />}>
                <InventoryStoresPage />
              </Suspense>
            ),
          },
        ],
      },

      // Administration routes
      {
        path: 'administration',
        children: [
          {
            path: 'users',
            element: (
              <Suspense fallback={<PageLoader />}>
                <AdministrationUsersPage />
              </Suspense>
            ),
          },
          {
            path: 'roles',
            element: (
              <Suspense fallback={<PageLoader />}>
                <AdministrationRolesPage />
              </Suspense>
            ),
          },
          {
            path: 'working-years',
            element: (
              <Suspense fallback={<PageLoader />}>
                <AdministrationWorkingYearsPage />
              </Suspense>
            ),
          },
        ],
      },

      // Reporting routes
      {
        path: 'reporting',
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<PageLoader />}>
                <ReportingPage />
              </Suspense>
            ),
          },
          {
            path: 'expenses',
            element: (
              <Suspense fallback={<PageLoader />}>
                <ReportingExpensesPage />
              </Suspense>
            ),
          },
          {
            path: 'payroll',
            element: (
              <Suspense fallback={<PageLoader />}>
                <ReportingPayrollPage />
              </Suspense>
            ),
          },
          // Payroll reports
          { path: 'payroll/payroll-summary', element: <Suspense fallback={<PageLoader />}><ReportPayrollSummaryPage /></Suspense> },
          { path: 'payroll/payslip', element: <Suspense fallback={<PageLoader />}><ReportPayslipPage /></Suspense> },
          { path: 'payroll/task-detail', element: <Suspense fallback={<PageLoader />}><ReportTaskDetailPage /></Suspense> },
          { path: 'payroll/milling-report', element: <Suspense fallback={<PageLoader />}><ReportMillingReportPage /></Suspense> },
          { path: 'payroll/operation-rates-card', element: <Suspense fallback={<PageLoader />}><ReportOperationRatesCardPage /></Suspense> },
          { path: 'payroll/production-report', element: <Suspense fallback={<PageLoader />}><ReportProductionReportPage /></Suspense> },
          { path: 'payroll/cost-of-production', element: <Suspense fallback={<PageLoader />}><ReportCostOfProductionPage /></Suspense> },
          { path: 'payroll/labour-productivity', element: <Suspense fallback={<PageLoader />}><ReportLabourProductivityPage /></Suspense> },
          { path: 'payroll/block-performance', element: <Suspense fallback={<PageLoader />}><ReportBlockPerformancePage /></Suspense> },
          { path: 'payroll/annual-summary', element: <Suspense fallback={<PageLoader />}><ReportAnnualSummaryPage /></Suspense> },
          // Personnel reports
          { path: 'personnel/employee-register', element: <Suspense fallback={<PageLoader />}><ReportEmployeeRegisterPage /></Suspense> },
          { path: 'personnel/headcount', element: <Suspense fallback={<PageLoader />}><ReportHeadcountPage /></Suspense> },
          { path: 'personnel/advances-loans', element: <Suspense fallback={<PageLoader />}><ReportAdvancesLoansPage /></Suspense> },
          { path: 'personnel/deduction-aging', element: <Suspense fallback={<PageLoader />}><ReportDeductionAgingPage /></Suspense> },
          { path: 'personnel/employment-history', element: <Suspense fallback={<PageLoader />}><ReportEmploymentHistoryPage /></Suspense> },
          // Estates reports
          { path: 'estates/estate-profile', element: <Suspense fallback={<PageLoader />}><ReportEstateProfilePage /></Suspense> },
          { path: 'estates/block-productivity', element: <Suspense fallback={<PageLoader />}><ReportBlockProductivityPage /></Suspense> },
          { path: 'estates/operation-performance', element: <Suspense fallback={<PageLoader />}><ReportOperationPerformancePage /></Suspense> },
          // Inventory reports
          { path: 'inventory/stock-levels', element: <Suspense fallback={<PageLoader />}><ReportStockLevelsPage /></Suspense> },
          { path: 'inventory/stock-movement', element: <Suspense fallback={<PageLoader />}><ReportStockMovementPage /></Suspense> },
          { path: 'inventory/procurement', element: <Suspense fallback={<PageLoader />}><ReportProcurementPage /></Suspense> },
          { path: 'inventory/requisitions', element: <Suspense fallback={<PageLoader />}><ReportRequisitionsPage /></Suspense> },
          { path: 'inventory/vendor-summary', element: <Suspense fallback={<PageLoader />}><ReportVendorSummaryPage /></Suspense> },
          // Expenses reports
          { path: 'expenses/expense-summary', element: <Suspense fallback={<PageLoader />}><ReportExpenseSummaryPage /></Suspense> },
          { path: 'expenses/expense-detail', element: <Suspense fallback={<PageLoader />}><ReportExpenseDetailPage /></Suspense> },
          { path: 'expenses/expense-by-estate', element: <Suspense fallback={<PageLoader />}><ReportExpenseByEstatePage /></Suspense> },
          // Sales reports
          { path: 'sales/inventory-balances', element: <Suspense fallback={<PageLoader />}><ReportSalesInventoryBalancesPage /></Suspense> },
          { path: 'sales/production-vs-allocation', element: <Suspense fallback={<PageLoader />}><ReportProductionVsAllocationPage /></Suspense> },
          { path: 'sales/bottling-efficiency', element: <Suspense fallback={<PageLoader />}><ReportBottlingEfficiencyPage /></Suspense> },
          { path: 'sales/rejection-rate', element: <Suspense fallback={<PageLoader />}><ReportRejectionRatePage /></Suspense> },
          { path: 'sales/margin-by-channel', element: <Suspense fallback={<PageLoader />}><ReportMarginByChannelPage /></Suspense> },
          { path: 'sales/fg-turnover', element: <Suspense fallback={<PageLoader />}><ReportFgTurnoverPage /></Suspense> },
          // Validated-cycle history
          { path: 'payroll/farm-sales-history', element: <Suspense fallback={<PageLoader />}><ReportFarmSalesHistoryPage /></Suspense> },
          { path: 'payroll/cycle-deductions', element: <Suspense fallback={<PageLoader />}><ReportCycleDeductionsPage /></Suspense> },
          { path: 'payroll/prepayments-history', element: <Suspense fallback={<PageLoader />}><ReportPrepaymentsHistoryPage /></Suspense> },
          { path: 'payroll/prepayment-audit', element: <Suspense fallback={<PageLoader />}><ReportPrepaymentAuditPage /></Suspense> },
        ],
      },

      // Settings routes
      {
        path: 'settings',
        children: [
          {
            path: 'salary-items',
            element: (
              <Suspense fallback={<PageLoader />}>
                <SettingsSalaryItemsPage />
              </Suspense>
            ),
          },
          {
            path: 'payroll-settings',
            element: (
              <Suspense fallback={<PageLoader />}>
                <SettingsPayrollSettingsPage />
              </Suspense>
            ),
          },
          {
            path: 'preferences',
            element: (
              <Suspense fallback={<PageLoader />}>
                <SettingsPreferencesPage />
              </Suspense>
            ),
          },
          {
            path: 'momo-operators',
            element: (
              <Suspense fallback={<PageLoader />}>
                <SettingsMoMoOperatorsPage />
              </Suspense>
            ),
          },
          {
            path: 'mobile-provision',
            element: (
              <Suspense fallback={<PageLoader />}>
                <SettingsMobileProvisionPage />
              </Suspense>
            ),
          },
          {
            path: 'statutory-rates',
            element: (
              <Suspense fallback={<PageLoader />}>
                <SettingsStatutoryRatesPage />
              </Suspense>
            ),
          },
          {
            path: 'security',
            element: (
              <Suspense fallback={<PageLoader />}>
                <SettingsSecurityPage />
              </Suspense>
            ),
          },
          {
            path: 'system-settings',
            element: (
              <Suspense fallback={<PageLoader />}>
                <SettingsSystemSettingsPage />
              </Suspense>
            ),
          },
        ],
      },

      // Search
      {
        path: 'search',
        element: (
          <Suspense fallback={<PageLoader />}>
            <SearchPage />
          </Suspense>
        ),
      },

      // Help
      {
        path: 'help',
        element: (
          <Suspense fallback={<PageLoader />}>
            <HelpPage />
          </Suspense>
        ),
      },

      // 403 Forbidden
      {
        path: 'forbidden',
        element: (
          <Suspense fallback={<PageLoader />}>
            <ForbiddenPage />
          </Suspense>
        ),
      },

      // 404 fallback
      {
        path: '*',
        element: <NotFoundPage />,
      },
    ],
  },

  // Public routes (no navbar/layout)
  {
    path: 'welcome',
    element: <WelcomePage />,
  },
  {
    path: 'login',
    element: <Navigate to="/welcome" replace />,
  },
  {
    path: 'sign-in',
    element: (
      <PublicRoute>
        <SignInPage />
      </PublicRoute>
    ),
  },
  {
    path: 'forgot-password',
    element: (
      <PublicRoute>
        <ForgotPasswordPage />
      </PublicRoute>
    ),
  },
  {
    path: 'reset-password',
    element: (
      <PublicRoute>
        <ResetPasswordPage />
      </PublicRoute>
    ),
  },
  {
    path: 'accept-invite',
    element: (
      <PublicRoute>
        <AcceptInvitePage />
      </PublicRoute>
    ),
  },
  {
    path: 'verify-2fa',
    element: (
      <PublicRoute>
        <Verify2faPage />
      </PublicRoute>
    ),
  },
  {
    path: 'mfa/enroll',
    element: <MfaEnrollPage />,
  },

  // change-password: authenticated users only, but outside the RootLayout shell
  {
    path: 'change-password',
    element: (
      <ProtectedRoute>
        <ChangePasswordPage />
      </ProtectedRoute>
    ),
  },
]);
